import React, { useContext, useEffect, forwardRef, useImperativeHandle, useState, useCallback } from 'react'
import { useSelector } from 'react-redux';
import { selectSessionConfig } from "../../../../store/store-selector";
import { usePezodeFormData } from '../../../../hooks/usePezodeFormData';
import { DisplaySpinnerContext, RouteGuardContext, TabPanelContext } from "../../../../context/ContextIndex";
import { getPezodeDefaultValue, getPezodeSchema } from './schema';
import { useTranslation } from 'react-i18next';
import { AppForm, DynamicFormContainer, notifyToast, ToastType } from '../../../../components';
import { useFormContext } from 'react-hook-form';
import { PricingPortalAPI } from '../../../../apis/pricing-api';
import { getPezodeRange, getPezodeProductMap } from "../../api-handler/api-handler";
import { getPezodeRangeDefaultValue } from "../../pezode-range/create/schema";
import { getPezodeProductDefaultValue } from "../../pezode-product/create/schema";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import _ from "lodash";

const PezodeCreate = forwardRef(({ setPezodeFormData, setIsFormValid }, ref) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const { formState, clearErrors, watch, setValue } = useFormContext();
    const _tabContextObject = useContext(TabPanelContext);
    const _tabContext = _tabContextObject.getTabFormContext();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const routeGuardContext = useContext(RouteGuardContext);

    const EditPezodeId = searchParams.get("pezodeid");
    const navigationData = location.state;
    const settingsConfig = useSelector(selectSessionConfig);

    const createPezodeLookupData = usePezodeFormData(settingsConfig.businessCode, settingsConfig.countryCode);

    const _formStateData = watch();

    const [availableInfestation, setAvailableInfestation] = useState([]);

    useImperativeHandle(ref, () => ({
        tabChangeHandler,
    }));

    const tabChangeHandler = (newtab) => {
        if (newtab === "1" && formState.isValid) {
            onPezodeSubmit(newtab);
        } else if (newtab === "2" && _tabContext?.data?.pezoderange?.length > 0) {
            onPezodeSubmit(newtab);
        }
    };

    //on pezode next
    const onPezodeSubmit = (newtab = "1") => {
        try {
            clearErrors();
            _tabContextObject.setTabFormContext({
                tabIndex: newtab,
                data: {
                    ..._tabContext.data,
                    pezodein: _formStateData,
                },
                isDirty: EditPezodeId ? !_.isEqual(_tabContext.prevData?.pezodein, _formStateData) : true,
            });
        } catch (e) {
            alert(e);
        }
    };

    //on pezode cancel
    const handleCancel = () => {
        try {
            navigate("/pezodelist");
        } catch (e) {
            alert(e);
        }
    };

    const setFormSchema = useCallback(async () => {
        try {
            let _lookUpData = {};
            _lookUpData.location = [];
            _lookUpData.zone = [];
            let _editFormData = navigationData ? { ...navigationData } : null;

            if (createPezodeLookupData?.infestationLevelList?.length) {
                _lookUpData.infestationLevel = createPezodeLookupData.infestationLevelList.map((item) => {

                    if (_editFormData && item.InfestationLevelId === _editFormData.InfestationLevelId) {
                        _editFormData.infestationLevel = [{
                            id: item.InfestationLevelId,
                            label: item.LevelDescription,
                        }];
                    }
                    return {
                        id: item.InfestationLevelId,
                        label: item.LevelDescription,
                    };
                });
            };
            if (createPezodeLookupData?.locationList?.length) {
                _lookUpData.location = createPezodeLookupData.locationList.map((item) => {

                    if (_editFormData && item.LocationId === _editFormData.LocationId) {
                        _editFormData.Location = {
                            id: item.LocationId,
                            label: item.LocationName,
                        };
                    }
                    return {
                        id: item.LocationId,
                        label: item.LocationName,
                    };
                });
            };


            if (createPezodeLookupData?.zoneList?.length) {
                _lookUpData.zone = createPezodeLookupData.zoneList.map((item) => {
                    if (_editFormData && item.ZoneLocationId === _editFormData.ZoneLocationId) {
                        _editFormData.Zone = {
                            id: item.ZoneLocationId,
                            label: item.ZoneName,
                        };
                    }
                    return {
                        id: item.ZoneLocationId,
                        label: item.ZoneName,
                    };
                });
            };

            if (createPezodeLookupData?.pestList?.length) {
                _lookUpData.pest = createPezodeLookupData.pestList.map((item) => {
                    if (_editFormData && item.ReqtCategoryID === parseInt(_editFormData.PestCode)) {
                        _editFormData.Pest = {
                            id: item.ReqtCategoryID,
                            label: item.ReqtCategoryDesc,
                        };
                    }
                    return {
                        id: item.ReqtCategoryID,
                        label: item.ReqtCategoryDesc,
                    };
                });
            };

            if (createPezodeLookupData?.deviceList?.length) {
                _lookUpData.device = createPezodeLookupData.deviceList.map((item) => {
                    if (_editFormData && _editFormData.DeviceId && item.DeviceId === _editFormData.DeviceId) {
                        _editFormData.Device = {
                            id: item.DeviceId,
                            label: item.shortName,
                        };
                    }
                    return {
                        id: item.DeviceId,
                        label: item.shortName,
                    };
                });
            };

            if (createPezodeLookupData?.pezodeUnitList?.length) {
                _lookUpData.inputUnit = createPezodeLookupData.pezodeUnitList.map((item) => {
                    if (_editFormData && item.UnitKey === _editFormData.InputUnit) {
                        _editFormData.InputUnit = {
                            id: item.UnitKey,
                            label: item.UnitValue,
                        };
                    }
                    return {
                        id: item.UnitKey,
                        label: item.UnitValue,
                    };
                });
            };

            if (createPezodeLookupData?.icabsProductList?.length) {
                _lookUpData.iCabsProductCode = createPezodeLookupData.icabsProductList.map((item) => {
                    if (_editFormData && item.ProductCode === _editFormData.ICABSProductCode) {
                        _editFormData.iCabsProductCode = {
                            id: item.ProductCode,
                            label: item.ProductCode + " - " + item.ProductDesc,
                        };
                    }
                    return {
                        id: item.ProductCode,
                        label: item.ProductCode + " - " + item.ProductDesc,
                    };
                });
            };

            const icabsDetail = _formStateData?.iCabsProductCode ? _formStateData?.iCabsProductCode : _editFormData?.iCabsProductCode;
            const _productDetail = icabsDetail ? createPezodeLookupData.icabsProductCoverList
                .filter((item) => item.ProductCode && icabsDetail.id === item.ProductCode)
                .map((item) =>
                    createPezodeLookupData.icabsProductDetailList.find(
                        (detailItem) => detailItem.ProductDetailCode === item.ProductDetailCode
                    )
                )
                .filter(Boolean) : [];

            console.log(" product detail " + JSON.stringify(_productDetail));
            if (_productDetail.length) {
                _editFormData = _editFormData || {};
                _editFormData.iCabsProductDetails = [];

                const productCodes = _editFormData.ICABSProductDetailCode
                    ? _editFormData.ICABSProductDetailCode.split(",").map(code => code.trim())
                    : [];

                _lookUpData.iCabsProductDetails = _productDetail.map((item) => {
                    if (productCodes.includes(item.ProductDetailCode)) {
                        _editFormData.iCabsProductDetails.push({
                            id: item.ProductDetailCode,
                            label: item.ProductDetailCode,
                        });
                    }

                    return {
                        id: item.ProductDetailCode,
                        label: item.ProductDetailCode,
                    };
                });
            }


            // setFormLookupData(_lookUpData);

            if (_editFormData && EditPezodeId) {
                _tabContextObject.setTabFormContext({
                    editPezodeId: EditPezodeId,
                    prevData: {
                        pezodein: getPezodeDefaultValue(_editFormData),
                    },
                    formLookupData: _lookUpData,
                    isDirty: false,
                });
                setPezodeFormData((prevstate) => ({
                    ...prevstate,
                    schema: getPezodeSchema(_lookUpData, _editFormData, EditPezodeId),
                    appformdata: getPezodeDefaultValue(_tabContext.data?.pezodein ? _tabContext.data?.pezodein : _editFormData),
                }));
            } else {
                _tabContextObject.setTabFormContext({
                    formLookupData: _lookUpData,
                });
                setPezodeFormData((prevState) => ({
                    ...prevState,
                    pezodeSchema: getPezodeSchema(_lookUpData, _tabContext.data?.pezodein ? _tabContext.data?.pezodein : _formStateData, EditPezodeId, availableInfestation)
                }));
            }

        } catch (err) {
            alert(err);
        }
    }, [createPezodeLookupData, navigationData, EditPezodeId, _formStateData, _tabContext.data?.pezodein, _tabContextObject, setPezodeFormData, availableInfestation]); // Added dependencies);

    const fetchPezodeRanges = useCallback(async () => {
        try {
            if (EditPezodeId && !_tabContext?.data?.pezoderange) {
                // Make the API call here to get the pezode range
                let response = await getPezodeRange(EditPezodeId, settingsConfig);

                const formattedResponse = response.map(item => ({
                    ...item,
                    OutputUnit: {
                        id: item.OutputUnit,
                        label: item.OutputUnit
                    }
                }));

                // Store the fetched data in the context
                _tabContextObject.setTabFormContext({
                    data: {
                        pezoderange: formattedResponse.map((item, index) => ({
                            id: index,
                            ...getPezodeRangeDefaultValue(item)
                        })),
                    },
                    prevData: {
                        pezoderange: formattedResponse,
                    },
                });
                // spinnerCtx.setLoadingIndicator(false);
            }

        } catch (err) {
            alert("Failed to fetch pezode range.");
        }
    }, [EditPezodeId, settingsConfig, _tabContextObject, _tabContext?.data?.pezoderange]);

    const fetchPezodeProduct = useCallback(async () => {
        try {
            if (EditPezodeId && !_tabContext?.data?.consumableproductmap) {
                // Make the API call here to get the consumable product
                let response = await getPezodeProductMap(EditPezodeId, settingsConfig);

                const formattedResponse = response.map(responseItem => {
                    const match = createPezodeLookupData.consumableProductList.find(item => item.ConsumableProductId === responseItem.ConsumableProductId);
                    let _consumableProduct = {};
                    if (match) {
                        _consumableProduct = {
                            id: match.ConsumableProductId,
                            label: match.ShortName
                        };
                    }
                    return {
                        ...responseItem,
                        ConsumableProduct: _consumableProduct
                    }
                });

                // Store the fetched data in the context
                _tabContextObject.setTabFormContext({
                    data: {
                        consumableproductmap: formattedResponse.map((item, index) => ({
                            id: index,
                            ...getPezodeProductDefaultValue(item)
                        })),
                    },
                    prevData: {
                        consumableproductmap: formattedResponse,
                    },
                });
                // spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert("Failed to fetch pezode product.");
        }
    }, [EditPezodeId, settingsConfig, _tabContextObject, createPezodeLookupData, _tabContext?.data?.consumableproductmap]);
    //Use Effects
    useEffect(() => {
        spinnerCtx.setLoadingIndicator(true); // Start loading
        const fetchData = async () => {
            try {
                await setFormSchema(); // Run synchronous function first
                await fetchPezodeRanges();
                if (createPezodeLookupData?.consumableProductList?.length) {
                    await fetchPezodeProduct();
                }
            } catch (err) {
                console.error("Error in fetching data:", err);
            } finally {
                spinnerCtx.setLoadingIndicator(false); // Stop loading
            }
        };

        if (createPezodeLookupData?.locationList?.length > 0 && createPezodeLookupData?.pestList?.length > 0 &&
            createPezodeLookupData?.deviceList?.length > 0 && createPezodeLookupData?.zoneList?.length > 0 &&
            createPezodeLookupData?.infestationLevelList?.length > 0 && createPezodeLookupData?.consumableProductList?.length > 0 &&
            createPezodeLookupData?.icabsProductList?.length && createPezodeLookupData?.icabsProductCoverList?.length &&
            createPezodeLookupData?.icabsProductDetailList?.length > 0 && createPezodeLookupData?.pezodeUnitList?.length > 0) {
            fetchData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createPezodeLookupData, EditPezodeId, navigationData]);

    //to check if pezode combination exists
    useEffect(() => {

        if (_.isEqual(_tabContext.data?.pezodein, _formStateData)) return;
        const fetchAvailablePezode = async (filter) => {
            try {
                setValue("infestationLevel", null);
                let availableInfestation = [];
                let _availablePezode = await PricingPortalAPI.getFilterList(
                    settingsConfig.businessCode,
                    settingsConfig.countryCode,
                    "getPezodeList",
                    "pezodein",
                    filter
                );

                availableInfestation = _availablePezode.map((item) => item.InfestationLevelId);
                setAvailableInfestation(availableInfestation);
                const lookupData = _tabContextObject.getTabFormContext()?.formLookupData;

                setPezodeFormData((prevState) => ({
                    ...prevState,
                    pezodeSchema: getPezodeSchema(
                        lookupData,
                        _formStateData,
                        EditPezodeId,
                        availableInfestation
                    )
                }));

                spinnerCtx.setLoadingIndicator(false);
                if (_availablePezode && _availablePezode.length >= 3) {
                    notifyToast("Pezode combination already exists", ToastType.ERROR);
                }
            } catch (e) {
                spinnerCtx.setLoadingIndicator(false);
                alert(e);
            }
        };

        if (!!_formStateData?.Location?.id && !!_formStateData?.Pest?.id && !!_formStateData?.Device?.id) {
            let _filterClause = `&locationid=${_formStateData?.Location?.id}&deviceid=${_formStateData?.Device?.id}&pestcode=${_formStateData?.Pest?.id}&istoxic=${_formStateData?.Toxic}`;
            if (!!_formStateData?.Zone?.id) {
                _filterClause = _filterClause + `&zonelocationid=${_formStateData?.Zone?.id}`;
            }

            if (!EditPezodeId) {
                spinnerCtx.setLoadingIndicator(true);
                fetchAvailablePezode(_filterClause);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _formStateData.Location,
        _formStateData.Pest,
        _formStateData.Device,
        _formStateData.Zone,
        _formStateData.Toxic
    ]);

    //to reset zone once location is selected/changed
    useEffect(() => {
        if (!EditPezodeId) {
            if (_.isEqual(_tabContext.data?.pezodein, _formStateData)) return;
            let _lookUpData = _tabContextObject.getTabFormContext()?.formLookupData;

            if (!_lookUpData || Object.keys(_lookUpData).length === 0) {
                return; // or return null / early exit based on context
            }
            // Update zones for selected location
            const filteredZones = _formStateData.Location
                ? createPezodeLookupData.zoneList.filter(
                    (zone) => zone.LocationId === _formStateData.Location.id
                )
                : [];

            if (filteredZones.length) {
                _lookUpData.zone = filteredZones.map((item) => {
                    return {
                        id: item.ZoneLocationId,
                        label: item.ZoneName,
                    };
                });
            };

            setValue("Zone", null);
            // setFormLookupData(_lookUpData)
            _tabContextObject.setTabFormContext({
                formLookupData: _lookUpData,
            });
            setPezodeFormData((prevState) => ({
                ...prevState,
                pezodeSchema: getPezodeSchema(_lookUpData, _formStateData)
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _formStateData.Location
    ]);


    //to reset icabs product detail code
    useEffect(() => {
        if (_.isEqual(_tabContext.data?.pezodein, _formStateData)) return;
        let _lookUpData = _tabContextObject.getTabFormContext()?.formLookupData;
        if (!_lookUpData || Object.keys(_lookUpData).length === 0) {
            return; // or return null / early exit based on context
        }
        // Update icabs product details for selected icabs product code

        const _productDetail = _formStateData.iCabsProductCode
            ? createPezodeLookupData.icabsProductCoverList
                .filter((item) => item.ProductCode && _formStateData.iCabsProductCode.id === item.ProductCode)
                .map((item) =>
                    createPezodeLookupData.icabsProductDetailList.find(
                        (detailItem) => detailItem.ProductDetailCode === item.ProductDetailCode
                    )
                )
                .filter(Boolean) : [];

        //console.log(" product detail " + JSON.stringify(_productDetail));
        if (_productDetail.length) {
            _lookUpData.iCabsProductDetails = _productDetail.map((item) => {
                return {
                    id: item.ProductDetailCode,
                    label: item.ProductDetailCode,
                };
            });
        } else
            _lookUpData.iCabsProductDetails = [];

        // setValue("iCabsProductDetails", null);
        // setFormLookupData(_lookUpData)
        _tabContextObject.setTabFormContext({
            formLookupData: _lookUpData,
        });
        setPezodeFormData((prevState) => ({
            ...prevState,
            pezodeSchema: getPezodeSchema(_lookUpData, _formStateData, EditPezodeId, availableInfestation)
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _formStateData.iCabsProductCode
    ]);

    useEffect(() => {
        routeGuardContext.setFormState(_tabContext?.isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    return (
        <AppForm onSubmit={onPezodeSubmit} setFormValid={setIsFormValid}>
            <DynamicFormContainer
                header={t("createPezode.pezode")}
                onCancel={handleCancel}
                onSaveLabel={t("generic.next")}
                isWithAppForm={false}
                onSubmit={onPezodeSubmit}
            />
        </AppForm>
    )
});

export default PezodeCreate